import { ClubButton } from './ClubButton'

interface DuesNoticeProps {
  /** True once Stripe has already tried to collect and failed. */
  inArrears: boolean
  onAddCard: () => void
  busy?: boolean
}

/**
 * The renewal cliff (docs/fireside/stripe-setup.md §12.7).
 *
 * A standing taken up with a 100%-off code collects no card — Stripe has
 * nothing to ask for when the total is $0 — so a beta member sails through a
 * full year in perfectly good standing and then their first real invoice
 * arrives with nothing to charge. This is the club saying so while there is
 * still time to do something about it, rather than after the lapse.
 *
 * A solid brass frame, deliberately unlike EmptyPlate's dashed one: dashed
 * means "nothing here yet", and this is the opposite — something is here and it
 * wants the member.
 *
 * No dismiss control. There is no "remind me later" state to persist, and a
 * notice a member can quietly switch off is a notice that fails on the one day
 * it mattered.
 */
export function DuesNotice({ inArrears, onAddCard, busy = false }: DuesNoticeProps) {
  return (
    <div
      role="status"
      className="mt-9 rounded-club-lg border border-brass bg-paper px-6 py-5 sm:flex sm:items-center sm:justify-between sm:gap-8"
    >
      <div>
        <h3 className="font-display text-[17px] font-medium leading-tight text-pine">
          {inArrears ? 'Your dues did not go through.' : 'The club holds no card for you.'}
        </h3>
        <p className="mt-1.5 max-w-[56ch] text-[13.5px] text-ink-soft">
          {inArrears
            ? 'Your standing is on hold until they settle. A card puts it right again — your number stays struck either way.'
            : 'Your standing was taken up without one, so there is nothing to charge when it renews. A minute now saves the lapse later.'}
        </p>
      </div>
      <ClubButton
        variant="flag"
        size="sm"
        onClick={onAddCard}
        disabled={busy}
        className="mt-4 shrink-0 sm:mt-0"
      >
        {busy ? 'Opening…' : 'Add a card'}
      </ClubButton>
    </div>
  )
}
